'use client';

import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { REGEXP_ONLY_DIGITS } from 'input-otp';

import { Input } from '@/src/shared/ui/input';
import { Label } from '@/src/shared/ui/label';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/src/shared/ui/input-otp';
import type { MfaChallengeFormValues, MfaMethod } from '../model/types';

type Props = {
  control: Control<MfaChallengeFormValues>;
  errors: FieldErrors<MfaChallengeFormValues>;
  method: MfaMethod;
  // The API rejected the last code; the form shows that message.
  rejected: boolean;
};

const otpSlotClassName =
  'h-11 min-w-0 flex-1 rounded-lg border border-muted-foreground/70 bg-background text-base shadow-xs first:rounded-lg last:rounded-lg first:border-l border-y border-r border-l';

export function MfaCodeField({ control, errors, method, rejected }: Props) {
  const t = useTranslations('auth.mfa');
  const hasError = !!errors.code;
  const invalid = hasError || rejected;
  const describedBy = hasError ? 'mfa-code-error' : rejected ? 'mfa-api-error' : undefined;
  const formatMessage = method === 'backup' ? t('errors.backup_format') : t('errors.totp_format');

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="mfa-code" className="text-xs font-medium text-muted-foreground">
        {method === 'backup' ? t('backupLabel') : t('codeLabel')}
      </Label>
      <Controller
        name="code"
        control={control}
        render={({ field }) =>
          method === 'backup' ? (
            <Input
              id="mfa-code"
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              aria-invalid={invalid}
              aria-describedby={describedBy}
              className="h-11 border-muted-foreground/70 bg-background"
            />
          ) : (
            <InputOTP
              id="mfa-code"
              maxLength={6}
              pattern={REGEXP_ONLY_DIGITS}
              autoComplete="one-time-code"
              value={field.value}
              onChange={field.onChange}
              aria-invalid={invalid}
              aria-describedby={describedBy}
              containerClassName="w-full gap-3"
              className="w-full"
            >
              <InputOTPGroup className="flex flex-1 gap-2">
                <InputOTPSlot index={0} aria-invalid={invalid} className={otpSlotClassName} />
                <InputOTPSlot index={1} aria-invalid={invalid} className={otpSlotClassName} />
                <InputOTPSlot index={2} aria-invalid={invalid} className={otpSlotClassName} />
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup className="flex flex-1 gap-2">
                <InputOTPSlot index={3} aria-invalid={invalid} className={otpSlotClassName} />
                <InputOTPSlot index={4} aria-invalid={invalid} className={otpSlotClassName} />
                <InputOTPSlot index={5} aria-invalid={invalid} className={otpSlotClassName} />
              </InputOTPGroup>
            </InputOTP>
          )
        }
      />
      {hasError && (
        <p id="mfa-code-error" role="alert" className="text-xs text-destructive">
          {formatMessage}
        </p>
      )}
    </div>
  );
}
